import { useState, useCallback, useRef, useEffect } from 'react';

const MEMPOOL_BASE = 'https://mempool.space/api';
const ESPLORA_BASE = 'https://blockstream.info/api';

const HEIGHT_RE = /^\d+$/;
const HASH_RE = /^[0-9a-f]{64}$/i;

const normalizeBlock = (raw, source) => ({
  height: raw.height,
  hash: raw.id,
  timestamp: raw.timestamp,
  txCount: raw.tx_count,
  size: raw.size,
  weight: raw.weight,
  difficulty: raw.difficulty,
  nonce: raw.nonce,
  bits: raw.bits,
  merkleRoot: raw.merkle_root,
  previousBlockHash: raw.previousblockhash,
  pool: raw.extras?.pool?.name || null,
  totalFees: raw.extras?.totalFees ?? null,
  medianFee: raw.extras?.medianFee ?? null,
  feeRange: raw.extras?.feeRange || null,
  reward: raw.extras?.reward ?? null,
  avgFeeRate: raw.extras?.avgFeeRate ?? null,
  source,
});

const sumOutputs = (vout) => (vout || []).reduce((sum, o) => sum + (o.value || 0), 0);
const sumInputs = (vin) => (vin || []).reduce((sum, i) => sum + (i.prevout?.value || 0), 0);

const normalizeTx = (raw, source) => {
  const vinCount = (raw.vin || []).length;
  const voutCount = (raw.vout || []).length;
  const totalOut = sumOutputs(raw.vout);
  const totalIn = sumInputs(raw.vin);
  const isCoinbase = raw.vin?.[0]?.is_coinbase === true;
  const vsize = raw.weight ? raw.weight / 4 : null;
  const feeRate = raw.fee && vsize ? raw.fee / vsize : null;
  return {
    txid: raw.txid,
    size: raw.size,
    weight: raw.weight,
    vsize,
    fee: raw.fee,
    feeRate,
    vinCount,
    voutCount,
    totalIn,
    totalOut,
    isCoinbase,
    confirmed: raw.status?.confirmed === true,
    blockHeight: raw.status?.block_height ?? null,
    blockHash: raw.status?.block_hash || null,
    blockTime: raw.status?.block_time ?? null,
    source,
  };
};

const lookupBlock = async (base, input, signal) => {
  let url;
  const isV1 = base === MEMPOOL_BASE;
  if (HEIGHT_RE.test(input)) {
    url = isV1 ? `${base}/v1/blocks/${input}` : null;
    if (!url) return null;
  } else {
    url = isV1 ? `${base}/v1/block/${input.toLowerCase()}` : `${base}/block/${input.toLowerCase()}`;
  }
  const res = await fetch(url, { signal });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`block_${res.status}`);
  const data = await res.json();
  const raw = Array.isArray(data) ? data[0] : data;
  if (!raw || raw.error || raw.height == null) return null;
  return normalizeBlock(raw, isV1 ? 'mempool' : 'esplora');
};

const lookupBlockByHeightEsplora = async (input, signal) => {
  const hRes = await fetch(`${ESPLORA_BASE}/block-height/${input}`, { signal });
  if (hRes.status === 404) return null;
  if (!hRes.ok) throw new Error(`esplora_${hRes.status}`);
  const hash = (await hRes.text()).trim();
  if (!HASH_RE.test(hash)) return null;
  return lookupBlock(ESPLORA_BASE, hash, signal);
};

const lookupTx = async (base, txid, signal) => {
  const url = `${base}/tx/${txid.toLowerCase()}`;
  const res = await fetch(url, { signal });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`tx_${res.status}`);
  const raw = await res.json();
  if (!raw || !raw.txid) return null;
  return normalizeTx(raw, base === MEMPOOL_BASE ? 'mempool' : 'esplora');
};

export const useBlockLookup = () => {
  const [block, setBlock] = useState(null);
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const lookup = useCallback(async (rawInput) => {
    const input = (rawInput || '').trim();
    if (!input) return;

    const isHeight = HEIGHT_RE.test(input);
    const isHash = HASH_RE.test(input);

    if (!isHeight && !isHash) {
      setError('invalid');
      setBlock(null);
      setTx(null);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setError(null);
    setBlock(null);
    setTx(null);

    const setResult = (fn) => {
      if (!mountedRef.current || signal.aborted) return;
      fn();
    };

    try {
      if (isHeight) {
        const b = await lookupBlock(MEMPOOL_BASE, input, signal);
        if (b) return setResult(() => setBlock(b));
        const bEsp = await lookupBlockByHeightEsplora(input, signal);
        if (bEsp) return setResult(() => setBlock(bEsp));
        return setResult(() => setError('notfound'));
      }

      const b = await lookupBlock(MEMPOOL_BASE, input, signal);
      if (b) return setResult(() => setBlock(b));

      const t = await lookupTx(MEMPOOL_BASE, input, signal);
      if (t) return setResult(() => setTx(t));

      const bEsp = await lookupBlock(ESPLORA_BASE, input, signal);
      if (bEsp) return setResult(() => setBlock(bEsp));

      const tEsp = await lookupTx(ESPLORA_BASE, input, signal);
      if (tEsp) return setResult(() => setTx(tEsp));

      return setResult(() => setError('notfound'));
    } catch (err) {
      if (err.name === 'AbortError') return;
      setResult(() => setError('notfound'));
    } finally {
      if (mountedRef.current && !signal.aborted) setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setBlock(null);
    setTx(null);
    setError(null);
    setLoading(false);
  }, []);

  const fetchBlockByHash = useCallback(async (hash) => {
    if (!hash || !HASH_RE.test(hash)) return null;
    try {
      const b = await lookupBlock(MEMPOOL_BASE, hash);
      if (b) return b;
      return await lookupBlock(ESPLORA_BASE, hash);
    } catch {
      return null;
    }
  }, []);

  return { block, tx, loading, error, lookup, reset, fetchBlockByHash };
};
